import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-labelledby", "buttons", "children", "className", "columns", "defaultValue", "disabled", "fwdRef", "invalid", "name", "onBlur", "onChange", "size", "value", "wrap"],
  _excluded2 = ["aria-required", "required"];
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
import { FormContext } from '../form/form';
import { FormRowContext } from '../form-row/form-row';
// Add alias for merging labels, since classNames does the job well:
const mergeLabels = classNames;

/**
 * _A group of options that allows users to make a single selection from._
 *
 * ***
 *
 * The component is a group of radio buttons and offers props to organize and
 * display them in different ways. Contains [`<Option>`](#/Other%20Components/Option)
 * components as its children.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<fieldset>` element.
 */
export const RadioGroup = _ref => {
  let {
      'aria-labelledby': prevLabels,
      buttons = false,
      children,
      className,
      columns = 1,
      defaultValue = '',
      disabled: propDisabled,
      fwdRef,
      invalid,
      name: propName,
      onBlur,
      onChange,
      size: propSize,
      value: propValue,
      wrap
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [stateName] = useState(() => "ds-g-" + uuidv4());
  const [value, setValue] = useState('');
  const {
    spacing
  } = useContext(FormContext);
  const {
    disabled: rowDisabled,
    labelId,
    onGroup,
    onGroupItems,
    onHasRadiogroupRole,
    required: contextRequired,
    setAriaInvalidProp
  } = useContext(FormRowContext);
  const labels = mergeLabels(prevLabels, labelId);
  const size = propSize != null ? propSize : spacing;
  const selectedValue = propValue != null ? propValue : value || defaultValue;
  const {
      'aria-required': ariaRequired,
      required: propRequired
    } = props,
    restProps = _objectWithoutPropertiesLoose(props, _excluded2);
  // role radiogroup supports adding aria-required to the fieldset
  const required = !labels && (contextRequired || propRequired || ariaRequired && ariaRequired !== 'false');
  const handleChange = useCallback((ev, optOnChange) => {
    const {
      value: targetValue
    } = ev.target;
    setValue(targetValue);
    if (optOnChange) {
      optOnChange(ev, targetValue);
    }
    if (onChange) {
      onChange(ev, targetValue);
    }
    if (setAriaInvalidProp) {
      setAriaInvalidProp(undefined); // Reset aria-invalid value
    }
  }, [onChange, setAriaInvalidProp]);
  const handleBlur = useCallback((ev, optOnBlur) => {
    const {
      value: targetValue
    } = ev.target;
    if (optOnBlur) {
      optOnBlur(ev, targetValue);
    }
    if (onBlur) {
      onBlur(ev, targetValue);
    }
  }, [onBlur]);
  useEffect(() => onGroup && onGroup(), [onGroup]);
  useEffect(() => {
    onGroupItems == null || onGroupItems();
  }, [onGroupItems]);
  useEffect(() => setValue(propValue != null ? propValue : ''), [propValue]);
  useEffect(() => {
    onHasRadiogroupRole == null || onHasRadiogroupRole();
  }, [onHasRadiogroupRole]);
  useEffect(() => {
    if (setAriaInvalidProp && invalid) {
      setAriaInvalidProp(invalid);
    }
  }, [invalid, setAriaInvalidProp]);
  const contextValue = useMemo(() => ({
    buttons,
    columns,
    disabled: propDisabled || rowDisabled,
    group: true,
    handleBlur,
    handleChange,
    name: propName != null ? propName : stateName,
    selectedValue,
    size,
    wrap
  }), [buttons, columns, propDisabled, rowDisabled, propName, stateName, selectedValue, size, wrap, handleBlur, handleChange]);
  return /*#__PURE__*/React.createElement("fieldset", _extends({
    "aria-invalid": labels ? undefined : invalid // aria-invalid is handled in FormRow if inside one
    ,
    "aria-labelledby": prevLabels ? labels : undefined,
    "aria-required": required ? 'true' : undefined,
    role: labels ? undefined : 'radiogroup'
  }, restProps, {
    className: buildVersionedClassNames('ds-radio-group', className, {
      'ds-radio-group--buttonized': buttons && !wrap,
      'ds-radio-group--columned': columns > 1,
      'ds-radio-group--wrapped': wrap,
      ["ds-radio-group--" + size]: size !== 'default'
    }),
    ref: fwdRef
  }), /*#__PURE__*/React.createElement(RadioGroupContext.Provider, {
    value: contextValue
  }, children));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(RadioGroup, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'RadioGroup';
export default Fwd;
export const RadioGroupContext = /*#__PURE__*/createContext({
  group: false
});